import { useNavigate } from 'react-router-dom';
import { EmployeeFormDialog } from '@/modules/tenant/components/EmployeeFormDialog';
import { LeadFormSheet } from '@/modules/crm/components/LeadFormSheet';
import { QuoteFormSheet } from '@/modules/crm/components/QuoteFormSheet';
import { CustomerFormDialog } from '@/modules/tenant/components/CustomerFormDialog';
import { LiveFillCanvas } from '@/modules/inventory/components/canvas';
import { AssetCanvas } from '@/modules/inventory/components/asset-canvas/AssetCanvas';
import { WorkOrderFormSheet } from '@/modules/service/components/WorkOrderFormSheet';
import { useQuickAddStore } from '@/state/quickAddStore';

// Single mount point for quick-add dialogs/sheets. Mounted once in the
// tenant layout Wrapper. On create, navigates to the new record's detail page.
// Forms self-invalidate TanStack queries — no onSaved callback needed.
export function QuickAddHost() {
  const active = useQuickAddStore((s) => s.active);
  const close = useQuickAddStore((s) => s.close);
  const navigate = useNavigate();

  const handleOpenChange = (open: boolean) => {
    if (!open) close();
  };

  const goTo = (path: string) => {
    close();
    navigate(path);
  };

  // LiveFillCanvas is the sole add-boat surface (the legacy AddBoatWizard
  // has been retired).
  const boatOpen = active === 'boat';
  const handleBoatCreated = (id: string) => goTo(`/inventory/boats/${id}`);

  return (
    <>
      <EmployeeFormDialog
        open={active === 'employee'}
        onOpenChange={handleOpenChange}
        onCreated={(id) => goTo(`/employees/${id}`)}
      />

      {boatOpen && (
        <LiveFillCanvas
          open={boatOpen}
          onOpenChange={handleOpenChange}
          onSuccess={handleBoatCreated}
          // Modal entry (top-bar "+ → Add Boat") has no `?new` URL param, so
          // signal the blank-new entry explicitly or the hero ingest dropzone
          // never renders (it keys off `?new`/draftId/resumeDraftId otherwise).
          newEntry
        />
      )}

      {/* Engine/Trailer use the same AI-assisted AssetCanvas as the list pages'
          "New Engine"/"New Trailer" buttons. Mounted only while active so the
          canvas' heavy hooks/queries don't run on every page. */}
      {active === 'engine' && (
        <AssetCanvas
          type="engine"
          open
          onOpenChange={handleOpenChange}
          onCreated={(id) => goTo(`/inventory/engines/${id}`)}
        />
      )}
      {active === 'trailer' && (
        <AssetCanvas
          type="trailer"
          open
          onOpenChange={handleOpenChange}
          onCreated={(id) => goTo(`/inventory/trailers/${id}`)}
        />
      )}

      <LeadFormSheet
        open={active === 'lead'}
        onOpenChange={handleOpenChange}
        onCreated={(id) => goTo(`/crm/deals/${id}`)}
      />
      <QuoteFormSheet
        open={active === 'quote'}
        onOpenChange={handleOpenChange}
        onCreated={(id) => goTo(`/crm/quotes/${id}`)}
      />
      <CustomerFormDialog
        open={active === 'customer'}
        onOpenChange={handleOpenChange}
        onCreated={(id) => goTo(`/crm/customers/${id}`)}
      />

      {/* The Sheet navigates to the new record itself on success (plan
          09-06) and closes via onOpenChange — no onCreated here, or the
          record would be navigated to twice. */}
      <WorkOrderFormSheet
        open={active === 'work_order'}
        onOpenChange={handleOpenChange}
      />
    </>
  );
}
